import {
    DEFAULT_TIME_LIMIT,
    DESCRIPTION_CHARACTER_LIMIT,
    QUESTION_ANSWER_CHARACTER_LIMIT,
    TITLE_CHARACTER_LIMIT,
} from "./constants.ts";
import type {AnswerKey, BlooketQuestion, QuizContent} from "./types.ts";

export const ANSWER_KEYS: AnswerKey[] = ["A", "B", "C", "D"];

export interface BlooketQuestionInput {
    text: string;
    answers: BlooketAnswerInput[];
    timeLimit: number;
    randomizeAnswers: boolean;
}

interface BlooketAnswerInput {
    text: string;
    isCorrect: boolean;
}

// Map Blooket questions to the correct format for saving to Datastore.
export const mapBlooketQuestionsToSavableFormat = (
    questionSet: BlooketQuestion[],
): BlooketQuestionInput[] => {
    const reformatted = questionSet.map((q) => {
        const answers: BlooketAnswerInput[] = Object.entries(q.answers).map(
            ([key, value]) => {
                return {
                    text: value,
                    isCorrect: key === q.answerKey,
                };
            },
        );

        const question: BlooketQuestionInput = {
            text: q.prompt,
            timeLimit: DEFAULT_TIME_LIMIT,
            answers: answers,
            randomizeAnswers: false,
        };

        return question;
    });

    return reformatted;
};

export const hasHtmlTags: (str: string) => boolean = (str) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(str, "text/html");
    const textOnly =
        doc.body.childNodes.length === 1 &&
        doc.body.childNodes[0].nodeType === Node.TEXT_NODE;
    return !textOnly;
};

/**
 * Validates the title, description, questions, and answers of a Blooket Question Set
 * @param questionSetContent - the Blooket Question Set
 * @returns {boolean} A boolean dictating if the question set's content is valid. Will return `false` if undefined.
 */
export const isValidQuestionSet = (
    questionSetContent: QuizContent | undefined,
): boolean => {
    if (!questionSetContent) {
        return false;
    }

    if (!isValidText(questionSetContent.title, TITLE_CHARACTER_LIMIT)) {
        return false;
    }

    if (
        !isValidText(
            questionSetContent.description,
            DESCRIPTION_CHARACTER_LIMIT,
        )
    ) {
        return false;
    }

    if (!questionSetContent.questions.length) {
        return false;
    }

    for (const question of questionSetContent.questions) {
        if (!isValidText(question.prompt, QUESTION_ANSWER_CHARACTER_LIMIT)) {
            return false;
        }

        for (const key of ANSWER_KEYS) {
            if (
                !isValidText(
                    question.answers[key],
                    QUESTION_ANSWER_CHARACTER_LIMIT,
                )
            ) {
                return false;
            }
        }

        if (hasDuplicateAnswers(question)) {
            return false;
        }
    }
    return true;
};

const isValidText = (text: string, maxLength: number): boolean => {
    if (!text.trim().length) {
        return false;
    }

    if (text.length > maxLength) {
        return false;
    }

    if (hasHtmlTags(text)) {
        return false;
    }

    return true;
};

const hasDuplicateAnswers = (question: BlooketQuestion): boolean => {
    // The set constructor filters duplicates, so there are duplicates if and
    // only if there are fewer deduplicated answers than original answers.
    const answers = Object.values(question.answers);
    const deduplicated = new Set(answers);
    return deduplicated.size < answers.length;
};

export const getDuplicateKeys = (question: BlooketQuestion): Set<AnswerKey> => {
    const duplicateKeys: Set<AnswerKey> = new Set([]);
    Object.entries(question.answers).forEach(([key, text], _, all) => {
        // Check current text/key against all others.
        for (const [otherKey, otherText] of all) {
            // The text will always match if the key does.
            if (key !== otherKey && otherText === text) {
                duplicateKeys.add(key as AnswerKey);
                duplicateKeys.add(otherKey as AnswerKey);
            }
        }
    });
    return duplicateKeys;
};
